import b from 'b_';
import cx from 'classnames';
import { H2, Image, Text, UI_KIT_HEADER_TYPOGRAPHY, UI_KIT_TEXT_TYPOGRAPHY } from 'platform-components';
import React, { forwardRef } from 'react';

import { ItemContentProps } from 'common.components/widgets.content-showcase/content-showcase-widget';
import {
    BLOCK_SIZE,
    ContentShowcaseWidgetItemData,
    MAIN_ITEM_IMAGE_POSITION,
    MAIN_ITEM_ITEMS_DIRECTION,
} from 'common.components/widgets.content-showcase/typings';
import { accessibleClick } from '@edu-frontend/modules/src/shared/lib/a11y';

import { ContentShowcaseGradient } from '../__gradient/content-showcase__gradient';
import { ContentShowcaseIllustration } from '../__illustration/content-showcase__illustration';

import './content-showcase__main-item.scss';

interface Props {
    data: ContentShowcaseWidgetItemData;
    mix?: string;
    style?: React.CSSProperties;
    size: BLOCK_SIZE | null;
    ItemContent: (props: ItemContentProps) => JSX.Element;
    onTitleClick?: (props: ContentShowcaseWidgetItemData) => void;
}

// eslint-disable-next-line complexity
export const ContentShowcaseMainItem = forwardRef((props: Props, ref: React.Ref<HTMLDivElement>) => {
    const { data, ItemContent, mix, style, size, onTitleClick } = props;
    const { description, name, items, originalData, visualSettings } = data;

    const imageSrc = visualSettings?.image;
    const backgroundImage = visualSettings?.backgroundImage;
    const gradientColors = visualSettings?.colors;
    const bgColors = (gradientColors || []).filter(Boolean);

    const shouldUseImageAsBackground = Boolean(backgroundImage);
    const defaultBgImageSrc = 'https://e7n.s3.yandex.net/static/education/widgets/default-image.svg';
    const imagePosition =
        visualSettings?.imagePosition || items.length > 2
            ? MAIN_ITEM_IMAGE_POSITION.BOTTOM_LEFT
            : MAIN_ITEM_IMAGE_POSITION.TOP_RIGHT;

    const direction =
        visualSettings?.direction || items.length > 2
            ? MAIN_ITEM_ITEMS_DIRECTION.ROW
            : MAIN_ITEM_ITEMS_DIRECTION.COLUMN;

    const handleTitleClick = () => {
        if (onTitleClick) {
            onTitleClick(data);
        }
    };

    return (
        <div
            className={cx(b('content-showcase-widget', 'main-item', { direction, size, animated: true }), mix)}
            ref={ref}
            style={style}
        >
            <div className="content-showcase-widget__main-item-content">
                <div
                    className={b('content-showcase-widget', 'main-item-title', { link: Boolean(onTitleClick) })}
                    {...accessibleClick(handleTitleClick)}
                >
                    <H2 typography={UI_KIT_HEADER_TYPOGRAPHY.headlineLMedium}>{name}</H2>
                </div>
                {description && (
                    <div className="content-showcase-widget__main-item-description">
                        <Text typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphXLRegular}>{description}</Text>
                    </div>
                )}
            </div>
            <ItemContent block={originalData} mix={'content-showcase-widget__main-item-block'} />
            {imageSrc && (
                <ContentShowcaseIllustration
                    imageSrc={imageSrc}
                    key={imageSrc}
                    mix={b('content-showcase-widget', 'main-item-illustration', {
                        position: imagePosition,
                    })}
                />
            )}
            {!imageSrc && (
                <Image alt="" className={b('content-showcase-widget', 'main-item-bg-image')} src={defaultBgImageSrc} />
            )}
            <div className="content-showcase-widget__main-item-bg">
                {shouldUseImageAsBackground && (
                    <div className="content-showcase-widget__main-item-bg-img">
                        <svg height="100%" width="100%">
                            <defs>
                                <filter
                                    colorInterpolationFilters="sRGB"
                                    height="100%"
                                    id="bgBlur"
                                    width="100%"
                                    x="0"
                                    y="0"
                                >
                                    <feGaussianBlur stdDeviation="45" />
                                    <feGaussianBlur stdDeviation="25" />
                                </filter>
                            </defs>
                            <image
                                filter="url(#bgBlur)"
                                height="100%"
                                preserveAspectRatio="xMidYMid slice"
                                width="100%"
                                x="0"
                                xlinkHref={backgroundImage}
                                y="0"
                            />
                        </svg>
                    </div>
                )}
                <ContentShowcaseGradient colors={bgColors} />
            </div>
        </div>
    );
});
